import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { ConversionBand } from "@/components/system";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

/* "/#section" form so the anchors resolve from subpages too.

   "Approach" used to sit here pointing at /#services — the same href as
   "Services" two rows up. Two labels for one destination in a five-item list,
   duplicated again in the Navbar. Deleted in both places. */
const exploreLinks = [
  { label: "Work",     href: "/#showcase" },
  { label: "Services", href: "/#services" },
  { label: "How It Works", href: "/#engagement" },
  { label: "Contact",  href: "/#contact" },
];

/* The four service pages below linked only to each other — no inbound link
   from the homepage, nav or footer. Google reached them through the sitemap but
   they collected almost no internal link equity, which caps their ranking. The
   footer renders on all 16 routes, so listing them here fixes that site-wide
   without adding a word to the homepage. */
const serviceLinks = [
  { label: "AI Video Production",    href: "/ai-video-production" },
  { label: "Performance Creative",   href: "/performance-creative" },
  { label: "AI UGC Videos",          href: "/ai-ugc-videos" },
  { label: "AI Avatar Videos",       href: "/ai-avatar-videos" },
  { label: "AI Viral Content",       href: "/ai-viral-content" },
];

const solutionLinks = [
  { label: "Micro Drama Production", href: "/micro-drama-production" },
  { label: "Story Apps & OTT",       href: "/solutions/platforms" },
  { label: "Education",              href: "/solutions/education" },
  { label: "Growth for Brands",      href: "/solutions/growth" },
];

const caseStudyLinks = [
  { label: "Pocket FM", href: "/case-studies/pocket-fm" },
  { label: "Mazda",     href: "/case-studies/mazda" },
  { label: "Dashverse", href: "/case-studies/dashverse" },
];

const socialLinks = [
  { href: "https://instagram.com/kyrosity", label: "Instagram", icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true" className="h-5 w-5"><rect x="2" y="2" width="20" height="20" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" /></svg>
  )},
  { href: "https://linkedin.com/company/kyrosity", label: "LinkedIn", icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-5 w-5"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
  )},
  { href: "https://x.com/kyrosity", label: "X", icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-5 w-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  )},
  { href: "https://tiktok.com/@kyrosity", label: "TikTok", icon: <TikTokIcon aria-hidden="true" className="h-5 w-5" /> },
];

/* Scroll to an on-page contact section when one exists; otherwise fall through
   to the homepage contact anchor.

   This is delegated from the wrapper rather than bound to the anchor because
   the band is now the shared <ConversionBand> primitive, which owns its own
   markup. The behaviour is identical: a click on the band's "/#contact" link
   scrolls in place on any route that renders <Contact />. */
const scrollToLocalContact = (e) => {
  const link = e.target.closest?.('a[href="/#contact"]');
  if (!link) return;
  const el = document.getElementById("contact");
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth" });
  }
};

// min-h-11 (44px) rather than the py-1 this used to carry: the audit measured
// every footer link at 28px tall, well under the touch-target floor.
const linkClass =
  "flex min-h-11 items-center font-body text-body text-mute transition-colors duration-hover ease-gate hover:text-bone";

/* The column head lives here and nowhere else. There used to be two copies of
   the same 10px mono-caps rule — one inside LinkColumn, one hand-written above
   the Connect block — so a fix to the first silently missed the second.

   It is not the mono label any more either: that face is reserved for section
   eyebrows and data captions. A footer column head is neither. Sentence case at
   body size, separated from its links by colour rather than by size. */
const ColumnHead = ({ children }) => (
  <h3 className="mb-3 font-body text-body text-bone">{children}</h3>
);

const LinkColumn = ({ title, links, Component = Link }) => (
  <div>
    <ColumnHead>{title}</ColumnHead>
    <ul>
      {links.map((link) => (
        <li key={`${link.label}-${link.href}`}>
          {Component === Link ? (
            <Link to={link.href} className={linkClass}>{link.label}</Link>
          ) : (
            <a href={link.href} className={linkClass}>{link.label}</a>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export const Footer = () => (
  /* No `border-t` here: the band that follows it painted straight over it, so
     the rule was a dead edge. ConversionBand draws the separator itself. */
  <footer data-testid="footer-section" className="bg-ground">

    {/* The gold band used to be a hand-rolled full-bleed plane arriving as a
        hard cut from near-black, with its own rhythm (py-12, no md: step) and
        its own button. On the homepage the element before it is <Contact />,
        which made this the third consecutive ask, under a third name: the
        heading said "Start a project with us.", the button said "Let's talk",
        and the Explore column said "Contact". One ask, one name now. */}
    <div data-testid="footer-cta" onClick={scrollToLocalContact}>
      <ConversionBand lede="One brief. One call. We take it from there." />
    </div>

    {/* Main */}
    <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
      <div className="grid grid-cols-2 gap-10 md:grid-cols-4 lg:grid-cols-6">
        <div className="col-span-2">
          <Logo />
          <p className="mt-5 font-body text-body-l text-bone">
            Creative that earns the next test.
          </p>
          <p className="measure mt-4 font-body text-body text-mute">
            Performance creative, brand films, and story worlds for growth teams
            across India, the US, and beyond.
          </p>
        </div>

        <LinkColumn title="Explore" links={exploreLinks} Component="a" />
        <LinkColumn title="Services" links={serviceLinks} />
        <LinkColumn title="Solutions" links={solutionLinks} />

        <div>
          <LinkColumn title="Case Studies" links={caseStudyLinks} />
          <div className="mt-8">
            <ColumnHead>Connect</ColumnHead>
            {/* The address, set exactly like every other footer link. It used
                to be a third styling of the email link on a page that already
                had two. */}
            <a href="mailto:contact@kyrosity.ai" className={linkClass}>
              contact@kyrosity.ai
            </a>
            {/* flex-wrap, because the squares are 44px and must stay 44px: at six
                icons the row overflowed a 390px phone by 25px. Wrapping keeps
                the touch target and loses nothing. */}
            <ul className="mt-4 flex flex-wrap gap-2">
              {socialLinks.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase().replace(/\s+/, "-")}`}
                    /* 44px, the touch floor this same file already honours on
                       its text links. These were 40px squares with a border
                       that measured 1.35:1 — an edge that wasn't there. */
                    className="flex h-11 w-11 items-center justify-center border border-line text-mute transition-colors duration-hover ease-gate hover:border-gold hover:text-gold"
                  >
                    {s.icon}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom bar */}
    <div className="border-t border-rule">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-5 md:flex-row md:px-10">
        <p className="font-body text-caption text-faint">
          © {new Date().getFullYear()} Kyrosity
        </p>
        <div className="flex gap-2">
          <Link to="/privacy" className="flex min-h-11 items-center px-3 font-body text-caption text-mute transition-colors duration-hover ease-gate hover:text-bone">Privacy</Link>
          <Link to="/terms" className="flex min-h-11 items-center px-3 font-body text-caption text-mute transition-colors duration-hover ease-gate hover:text-bone">Terms</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
